import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { 
  Card, 
  Table as AntTable, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  message, 
  Tooltip,
  Form,
  Input,
  Switch,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  CheckCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
  SyncOutlined,
  TagOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import * as termsService from '../../services/termsServices';
//import './TermsAndConditionsList.css';

// Add some inline styles for the modal content
const modalStyles = {
  '.terms-content': {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: '0 10px',
  },
  '.terms-html-content': {
    margin: '20px 0',
    lineHeight: '1.6',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '1em 0 0.5em',
    },
    '& p': {
      margin: '0.5em 0',
    },
    '& ul, & ol': {
      margin: '0.5em 0',
      paddingLeft: '2em',
    },
  },
  '.terms-meta': {
    marginTop: '20px',
    paddingTop: '15px',
    borderTop: '1px solid #f0f0f0',
    '& p': {
      margin: '5px 0',
      color: '#666',
    },
  },
};

// Merge with existing styles
const mergedStyles = {
  ...modalStyles,
};

// Configure the editor with additional plugins and toolbar items
ClassicEditor.defaultConfig = {
  toolbar: {
    items: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      'bulletedList',
      'numberedList',
      '|',
      'fontFamily',
      'fontSize',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'outdent',
      'indent',
      '|',
      'blockQuote',
      'insertTable',
      '|',
      'undo',
      'redo'
    ]
  },
  table: {
    contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
  },
  // This value must be kept in sync with the language defined in webpack.config.js.
  language: 'en'
};

// Editor configuration with font features
const editorConfig = {
  toolbar: [
    'heading',
    '|',
    'bold',
    'italic',
    'link',
    '|',
    'bulletedList',
    'numberedList',
    '|',
    'fontFamily',
    'fontSize',
    'fontColor',
    'fontBackgroundColor',
    '|',
    'outdent',
    'indent',
    '|',
    'blockQuote',
    'insertTable',
    '|',
    'undo',
    'redo'
  ],
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
    ]
  },
  fontSize: {
    options: [
      'tiny',
      'small',
      'default',
      'big',
      'huge'
    ],
    supportAllValues: true
  },
  fontFamily: {
    options: [
      'Arial, sans-serif',
      'Courier New, Courier, monospace',
      'Georgia, serif',
      'Lucida Sans Unicode, Lucida Grande, sans-serif',
      'Tahoma, sans-serif',
      'Times New Roman, Times, serif',
      'Trebuchet MS, Helvetica, sans-serif',
      'Verdana, sans-serif',
      'inherit'
    ],
    supportAllValues: true
  },
  fontColor: {
    columns: 5,
    documentColors: 10,
    colors: [
      { color: 'hsl(0, 0%, 0%)', label: 'Black' },
      { color: 'hsl(0, 0%, 30%)', label: 'Dark Gray' },
      { color: 'hsl(0, 0%, 60%)', label: 'Gray' },
      { color: 'hsl(0, 0%, 90%)', label: 'Light Gray' },
      { color: 'hsl(0, 0%, 100%)', label: 'White' },
      { color: 'hsl(0, 75%, 60%)', label: 'Red' },
      { color: 'hsl(30, 75%, 60%)', label: 'Orange' },
      { color: 'hsl(60, 75%, 60%)', label: 'Yellow' },
      { color: 'hsl(90, 75%, 60%)', label: 'Light Green' },
      { color: 'hsl(120, 75%, 60%)', label: 'Green' },
      { color: 'hsl(150, 75%, 60%)', label: 'Aquamarine' },
      { color: 'hsl(180, 75%, 60%)', label: 'Turquoise' },
      { color: 'hsl(210, 75%, 60%)', label: 'Light Blue' },
      { color: 'hsl(240, 75%, 60%)', label: 'Blue' },
      { color: 'hsl(270, 75%, 60%)', label: 'Purple' }
    ]
  },
  fontBackgroundColor: {
    columns: 5,
    documentColors: 10,
    colors: [
      { color: 'hsl(0, 0%, 0%)', label: 'Black' },
      { color: 'hsl(0, 0%, 30%)', label: 'Dark Gray' },
      { color: 'hsl(0, 0%, 60%)', label: 'Gray' },
      { color: 'hsl(0, 0%, 90%)', label: 'Light Gray' },
      { color: 'hsl(0, 0%, 100%)', label: 'White' },
      { color: 'hsl(0, 75%, 60%)', label: 'Red' },
      { color: 'hsl(30, 75%, 60%)', label: 'Orange' },
      { color: 'hsl(60, 75%, 60%)', label: 'Yellow' },
      { color: 'hsl(90, 75%, 60%)', label: 'Light Green' },
      { color: 'hsl(120, 75%, 60%)', label: 'Green' },
      { color: 'hsl(150, 75%, 60%)', label: 'Aquamarine' },
      { color: 'hsl(180, 75%, 60%)', label: 'Turquoise' },
      { color: 'hsl(210, 75%, 60%)', label: 'Light Blue' },
      { color: 'hsl(240, 75%, 60%)', label: 'Blue' },
      { color: 'hsl(270, 75%, 60%)', label: 'Purple' }
    ]
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells'
    ]
  },
  placeholder: 'Enter terms and conditions content here...'
};

// Custom CKEditor component that works with Form.Item
const CKEditorField = ({ value, onChange }) => {
  const [editorError, setEditorError] = useState(null);

  const handleEditorReady = (editor) => {
    // Editor is ready to use
    console.log('CKEditor is ready to use!', editor);
  };

  const handleEditorChange = (event, editor) => {
    try {
      const data = editor.getData();
      if (onChange) {
        onChange(data);
      }
      setEditorError(null);
    } catch (error) {
      console.error('Error in editor change:', error);
      setEditorError('Error processing editor content');
    }
  };

  const handleEditorError = (error, { willEditorRestart }) => {
    console.error('CKEditor error:', error);
    setEditorError('Editor error occurred');
    
    if (willEditorRestart) {
      setEditorError(null);
    }
  };

  return (
    <div className="ckeditor-container" style={{ 
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {editorError && (
        <div style={{ 
          color: '#ff4d4f', 
          padding: '8px', 
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '4px',
          marginBottom: '8px'
        }}>
          {editorError}
        </div>
      )}
      <style>
        {`
          .ckeditor-container .ck-editor__editable {
            min-height: 350px !important;
            max-height: 500px !important;
            overflow-y: auto !important;
          }
        `}
      </style>
      <CKEditor
        editor={ClassicEditor}
        config={editorConfig}
        data={value || ''}
        onReady={handleEditorReady}
        onChange={handleEditorChange}
        onError={handleEditorError}
      />
    </div>
  );
};

// const { Title, Text } = Typography;
const { confirm } = Modal;

const TermsAndConditionsList = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const showModal = () => {
    if (terms.length === 0) {
      // First term - set to 1.0.0
      form.setFieldsValue({ version: '1.0.0' });
    } else {
      // Get the latest version and increment the patch number
      const latestVersion = terms[0]?.version || '0.0.0';
      const [major, minor, patch] = latestVersion.split('.').map(Number);
      const newVersion = `${major}.${minor}.${patch + 1}`;
      form.setFieldsValue({ version: newVersion });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      
      // Call your API to create new terms
      await termsService.createTerms({
        ...values,
        is_active: false,
      });
      
      message.success('Terms and conditions created successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchTerms(pagination.current, pagination.pageSize); // Refresh the list
    } catch (error) {
      console.error('Error creating terms:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create terms and conditions';
      message.error(errorMessage);
    } finally {
      setConfirmLoading(false);
    }
  };

  const fetchTerms = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const { current = 1, pageSize = 10, ...filters } = params;
      const response = await termsService.getTerms({
        page: current || 1,
        page_size: pageSize || 10,
        ...filters
      });

      console.log("response", response.data);
      setTerms(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        current: current || 1,
        pageSize: pageSize || 10
      }));
    } catch (error) {
      console.error('Error fetching terms:', error);
      message.error('Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  }, []);

  
  
  // Handle initial fetch and auth state changes
  useEffect(() => {
    const loadData = async () => {
      if (user) {
        await fetchTerms(1, pagination.pageSize);
      } else {
        // Redirect to login if not authenticated
        navigate('/login');
      }
    };
    
    loadData();
  }, [user, pagination.pageSize, fetchTerms, navigate]);

  const handleTableChange = (newPagination) => {
    fetchTerms({
      current: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [currentTerm, setCurrentTerm] = useState(null);

  const handleView = async (id) => {
    try {
      const term = await termsService.getTermById(id);
      setCurrentTerm(term);
      setViewModalVisible(true);
    } catch (error) {
      console.error('Error fetching term details:', error);
      message.error('Failed to load terms and conditions');
    }
  };

  const handleCloseModal = () => {
    setViewModalVisible(false);
    setCurrentTerm(null);
  };

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  //const [confirmLoading, setConfirmLoading] = useState(false);

  const handleEdit = async (id) => {
    try {
      const term = await termsService.getTermById(id);
      setEditingTerm(term);
      form.setFieldsValue({
        content: term.content,
        is_active: term.is_active
      });
      setIsEditModalVisible(true);
    } catch (error) {
      console.error('Error fetching term for editing:', error);
      message.error('Failed to load term for editing');
    }
  };

  const handleEditSubmit = async () => {
      try {
        setConfirmLoading(true);
        const values = await form.validateFields();
        await termsService.updateTerms(editingTerm.id, {
          content: values.content,
          is_active: values.is_active
        });
        message.success('Terms and conditions updated successfully');
        setIsEditModalVisible(false);
        setEditingTerm(null);
        form.resetFields();
        fetchTerms({ pagination });
        
      } catch (error) {
        console.error('Error updating terms:', error);
        if (error.response && error.response.data && error.response.data.detail) {
          message.error(`Update failed: ${error.response.data.detail}`);
        } else {
          message.error('Failed to update terms and conditions');
        }
      } finally {
        setConfirmLoading(false);
      }
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingTerm(null);
    form.resetFields();
  };

  // Delete confirmation modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingTerm, setDeletingTerm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (id) => {
    const term = terms.find(t => t.id === id);
    if (term) {
      setDeletingTerm(term);
      setDeleteModalVisible(true);
    }
  };

  const handleDelete = async () => {
    if (!deletingTerm) return;
    
    setDeleting(true);
    try {
      await termsService.deleteTerms(deletingTerm.id);
      message.success('Terms and conditions version deleted successfully');
      fetchTerms({ pagination });
      setDeleteModalVisible(false);
    } catch (error) {
      console.error('Error deleting terms and conditions:', error);
      message.error('Failed to delete terms and conditions version');
    } finally {
      setDeleting(false);
      setDeletingTerm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingTerm(null);
  };

  const handleActivate = async (id) => {
    try {
      await termsService.activateTerms(id);
      message.success('Terms and conditions activated successfully');
      fetchTerms({ pagination });
    } catch (error) {
      console.error('Error activating terms and conditions:', error);
      message.error('Failed to activate terms and conditions');
    }
  };

  const columns = [
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      sorter: true,
      width: 150,
      render: (text, record) => (
        <Space>
          <span style={{ minWidth: '50px', display: 'inline-block' }}>{text}</span>
          
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag icon={status === 'ACTIVE' ? <CheckCircleOutlined /> : status === 'INACTIVE' ? <ExclamationCircleOutlined /> : <CloseCircleOutlined />} color={status === 'ACTIVE' ? 'green' : status === 'INACTIVE' ? 'orange' : 'red'}>
          {status}
        </Tag>
      ),
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: true,
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => handleView(record.id)}
              style={{ marginRight: 8 }}
            />
          </Tooltip>
          {(user?.role === "SUPERUSER" || user?.role === "ADMIN") && (
            <>
              {record.status !== 'INACTIVE' && (
                <Tooltip title="Edit">
                  <Button 
                    icon={<EditOutlined />} 
                    onClick={() => handleEdit(record.id)}
                    style={{ marginRight: 8 }}
                  />
                </Tooltip>
              )}
              {!record.is_active && record.status !== 'EXPIRED' && (
                <>
                  <Tooltip title="Activate">
                    <Button 
                      type="primary" 
                      onClick={() => handleActivate(record.id)}
                      icon={<CheckCircleOutlined />}
                      style={{ marginRight: 8 }}
                    >
                      Activate
                    </Button>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <Button 
                      danger
                      onClick={() => handleDeleteClick(record.id)}
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  // Edit Modal
  const editModal = (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EditOutlined style={{ color: '#1890ff' }} />
          <span>Edit Terms and Conditions</span>
        </div>
      }
      open={isEditModalVisible}
      onOk={handleEditSubmit}
      onCancel={handleEditCancel}
      width={900}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      bodyStyle={{
        padding: '24px',
        //maxHeight: '70vh',
        overflowY: 'auto'
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: '',
          content: '',
          is_active: false
        }}
      >
        
        
        <Form.Item
          name="content"
          label="Content"
          rules={[
            { required: true, message: 'Please enter the content' }
          ]}
        >
          <CKEditorField />
        </Form.Item>
        
        <Form.Item
          name="is_active"
          valuePropName="checked"
        >
          <Switch 
            checkedChildren="Active" 
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  // Delete Confirmation Modal
  const deleteModal = (
    <Modal
      title="Confirm Deletion"
      open={deleteModalVisible}
      onOk={handleDelete}
      onCancel={handleDeleteCancel}
      confirmLoading={deleting}
      okText="Yes, Delete"
      okButtonProps={{ danger: true }}
      cancelText="Cancel"
    >
      <p>Are you sure you want to delete this terms and conditions version? This action cannot be undone.</p>
      {deletingTerm && (
        <p style={{ fontWeight: 'bold', marginTop: '10px' }}>
          Version: <code>{deletingTerm.version}</code>
        </p>
      )}
    </Modal>
  );

  return (
    <div className="terms-and-conditions-list">
      {editModal}
      {deleteModal}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>Terms and Conditions</span>
          </div>
        }
        open={viewModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button 
            key="close" 
            type="primary" 
            onClick={handleCloseModal}
            style={{ minWidth: 100 }}
          >
            Close
          </Button>
        ]}
        width={900}
        bodyStyle={{
          padding: 0,
          maxHeight: '70vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {currentTerm && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            {/* Header with title and status */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 500,
                  color: '#1f1f1f'
                }}>
                  {currentTerm.title}
                </h2>
                <Tag 
                  color={currentTerm.is_active ? 'green' : 'default'}
                  style={{
                    borderRadius: '12px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    padding: '0 8px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {currentTerm.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </div>
              
              <div style={{
                display: 'flex',
                gap: 24,
                fontSize: '0.85rem',
                color: '#595959'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FieldTimeOutlined />
                  <span>Created: {new Date(currentTerm.created_at).toLocaleString()}</span>
                </div>
                {currentTerm.updated_at && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <SyncOutlined />
                    <span>Updated: {new Date(currentTerm.updated_at).toLocaleString()}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <TagOutlined />
                  <span>v{currentTerm.version}</span>
                </div>
              </div>
            </div>

            {/* Content area */}
            <div 
              style={{
                padding: '24px',
                overflowY: 'auto',
                flex: 1,
                background: '#fff',
                lineHeight: 1.7,
                color: '#1f1f1f'
              }}
              className="terms-html-content"
              dangerouslySetInnerHTML={{ 
                __html: currentTerm.content 
                  ? currentTerm.content
                      .replace(/<h1/g, '<h1 style="font-size: 1.75rem; margin: 1.5em 0 1em; font-weight: 600; color: #1f1f1f;"')
                      .replace(/<h2/g, '<h2 style="font-size: 1.5rem; margin: 1.5em 0 1em; font-weight: 500; color: #1f1f1f;"')
                      .replace(/<h3/g, '<h3 style="font-size: 1.25rem; margin: 1.25em 0 0.75em; font-weight: 500; color: #1f1f1f;"')
                      .replace(/<p/g, '<p style="margin: 0 0 1em;"')
                      .replace(/<ul/g, '<ul style="margin: 0.5em 0 1em 1.5em; padding-left: 1.5em;"')
                      .replace(/<ol/g, '<ol style="margin: 0.5em 0 1em 1.5em; padding-left: 1.5em;"')
                      .replace(/<a/g, '<a style="color: #1890ff;"')
                  : ''
              }}
            />
          </div>
        )}
      </Modal>
      <Card 
        title="Terms and Conditions"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={showModal}
          >
            Add New Version
          </Button>
        }
      >
        {/* Create Terms Modal */}
        <Modal
          title="Create New Terms and Conditions"
          open={isModalVisible}
          onOk={() => form.submit()}
          confirmLoading={confirmLoading}
          onCancel={handleCancel}
          width={800}
          okText="Submit"
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              version: '',
              content: '',
              is_active: false
            }}
            onFinish={handleCreate}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="version"
                  label="Version"
                  rules={[{ required: true, message: 'Please enter version number' }]}
                >
                  <Input placeholder="e.g., 1.0.0" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="content"
                  label="Content"
                  rules={[
                    { 
                      required: true, 
                      message: 'Please enter terms and conditions content' 
                    },
                    {
                      min: 10,
                      message: 'Content must be at least 10 characters long'
                    }
                  ]}
                >
                  <CKEditorField />
                </Form.Item>
              </Col>
              {/* <Col span={24}>
                <Form.Item
                  name="is_active"
                  label="Active"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col> */}
            </Row>
          </Form>
        </Modal>
        <AntTable
          columns={columns}
          rowKey="id"
          dataSource={terms}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} versions`,
            pageSizeOptions: ['10', '25', '50', '100'],
            position: ['bottomRight']
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          bordered
          size="middle"
          className="custom-table"
          locale={{
            emptyText: 'No terms and conditions found',
            triggerDesc: 'Sort descending',
            triggerAsc: 'Sort ascending',
            cancelSort: 'Cancel sorting',
          }}
        />
      </Card>
    </div>
  );
};

export default TermsAndConditionsList;
